import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { CheckCircle2, Star, ThumbsUp, ThumbsDown, Copy, Check, ExternalLink } from "lucide-react";
import { isVisible, resolveBranch, pipe } from "@/lib/logic";
import { RichText, stripHtml, isHtml } from "@/components/RichText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicSurveyPage() {
  const { slug } = useParams();
  const [survey, setSurvey] = useState(null);
  const [step, setStep] = useState("intro"); // intro | q | thanks
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [respondent, setRespondent] = useState({ name: "", email: "", phone: "" });
  const [urlMeta, setUrlMeta] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [thankYouMsg, setThankYouMsg] = useState("");
  const [journey, setJourney] = useState(null);   // { redirect_url, redirect_label, coupon: {code,message} }
  const [lang, setLang] = useState("default");
  const [isPreview, setIsPreview] = useState(false);

  useEffect(() => {
    // Read preview flag from URL (used by Survey Builder iframe to render drafts)
    const previewFlag = (() => {
      try { return new URLSearchParams(window.location.search).get("preview") === "1"; } catch (_) { return false; }
    })();
    setIsPreview(previewFlag);
    const url = `${API}/public/surveys/${slug}` + (previewFlag ? "?preview=1" : "");
    axios.get(url)
      .then(r => {
        setSurvey(r.data);
        // Initialize answers with defaults
        const init = {};
        for (const q of (r.data.questions || [])) {
          if (q.default_value !== null && q.default_value !== undefined && q.default_value !== "") {
            init[q.id] = q.default_value;
          }
        }
        setAnswers(init);
        // Default language
        if (r.data.default_language && r.data.default_language !== "english" && r.data.translations?.[r.data.default_language]) {
          setLang(r.data.default_language);
        }
      })
      .catch((e) => {
        const detail = e?.response?.data?.detail;
        setErr(typeof detail === "string" ? detail : "This survey is not available.");
      });

    // Capture URL query parameters
    try {
      const params = new URLSearchParams(window.location.search);
      const meta = {};
      const known = { name: "name", email: "email", phone: "phone", mobile: "phone" };
      const respPatch = {};
      for (const [k, v] of params.entries()) {
        meta[k] = v;
        const lk = k.toLowerCase();
        if (known[lk]) respPatch[known[lk]] = v;
      }
      setUrlMeta(meta);
      if (Object.keys(respPatch).length) setRespondent((r) => ({ ...r, ...respPatch }));
    } catch (_) { /* ignore URL parse issues */ }
  }, [slug]);

  if (err && !survey) return <FullPage>{err}</FullPage>;
  if (!survey) return <FullPage>Loading…</FullPage>;

  // Language helpers
  const tr = lang !== "default" ? (survey.translations || {})[lang] : null;
  const isRtl = ["arabic", "hebrew"].includes(lang);
  const titleT = tr?.title || survey.title;
  const descT = tr?.description ?? survey.description;
  const welcomeT = tr?.welcome_message || survey.welcome_message;
  const thankT = tr?.thank_you_message || survey.thank_you_message;

  const total = survey.questions.length;
  const q = survey.questions[idx];
  const qTr = tr?.questions?.[q?.id] || {};
  const rawTitle = qTr.title || q?.title || "";
  const rawDesc = qTr.description ?? q?.description ?? "";
  const qTitle = pipe(rawTitle, answers, survey.questions);
  const qDesc = pipe(rawDesc, answers, survey.questions);
  const qPlaceholder = qTr.placeholder ?? q?.placeholder;
  const qOptions = qTr.options || q?.options;

  // Determine if we just entered a new page → show section header
  const sectionsByPage = Object.fromEntries((survey.sections || []).map((s) => [s.page, s]));
  const prevQ = idx > 0 ? survey.questions[idx - 1] : null;
  const enteredNewPage = !!q && (!prevQ || (q.page || 1) !== (prevQ.page || 1)) && (q.page || 1) > 1;
  const sectionHere = enteredNewPage ? sectionsByPage[q.page || 1] : null;

  const setAns = (qid, v) => setAnswers((a) => ({ ...a, [qid]: v }));

  const validateCurrent = () => {
    if (q.required) {
      const v = answers[q.id];
      if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) {
        setErr("This question is required."); return false;
      }
    }
    // Type-specific validation
    const v = answers[q.id];
    if (v !== undefined && v !== null && v !== "") {
      if (q.type === "email") {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v))) { setErr("Please enter a valid email address."); return false; }
      }
      if (q.type === "phone") {
        if (!/^[+]?[0-9 ()-]{7,20}$/.test(String(v))) { setErr("Please enter a valid phone number."); return false; }
      }
      if (q.type === "numeric") {
        const n = Number(v);
        if (Number.isNaN(n)) { setErr("Please enter a number."); return false; }
        const vd = q.validation || {};
        if (vd.min_value !== undefined && vd.min_value !== null && n < Number(vd.min_value)) { setErr(`Minimum is ${vd.min_value}.`); return false; }
        if (vd.max_value !== undefined && vd.max_value !== null && n > Number(vd.max_value)) { setErr(`Maximum is ${vd.max_value}.`); return false; }
      }
      if (q.type === "date") {
        const vd = q.validation || {};
        if (vd.min_date && String(v) < String(vd.min_date)) { setErr(`Earliest allowed: ${vd.min_date}.`); return false; }
        if (vd.max_date && String(v) > String(vd.max_date)) { setErr(`Latest allowed: ${vd.max_date}.`); return false; }
      }
      if (q.type === "file_upload" && q.allowed_extensions?.length) {
        const fname = typeof v === "string" ? v : (v?.name || "");
        const ext = fname.split(".").pop()?.toLowerCase();
        if (!q.allowed_extensions.map((e) => e.toLowerCase()).includes(ext)) {
          setErr(`Allowed: ${q.allowed_extensions.join(", ")}`); return false;
        }
      }
    }
    setErr("");
    return true;
  };

  const next = () => {
    if (!validateCurrent()) {
      // Ensure the error is visible even when the question is above the fold.
      setTimeout(() => {
        const el = document.querySelector('[data-testid="public-error"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 40);
      return;
    }
    const nextAnswers = { ...answers };
    const result = resolveBranch(q, nextAnswers[q.id], survey.questions, idx, nextAnswers);
    if (result.done) { submit(); return; }
    setIdx(result.next);
    setErr("");
  };

  const back = () => {
    setErr("");
    if (idx === 0) { setStep("intro"); return; }
    for (let i = idx - 1; i >= 0; i--) {
      if (isVisible(survey.questions[i], answers)) { setIdx(i); return; }
    }
    setStep("intro");
  };

  // Validate ALL visible questions (used on single-page submit and final stepper submit)
  const validateAll = () => {
    const visible = survey.questions.filter((qq) => isVisible(qq, answers));
    for (const qq of visible) {
      const v = answers[qq.id];
      if (qq.required && (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0))) {
        return { ok: false, qid: qq.id, msg: "This question is required." };
      }
      if (v !== undefined && v !== null && v !== "") {
        if (qq.type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v))) return { ok: false, qid: qq.id, msg: "Please enter a valid email address." };
        if (qq.type === "phone" && !/^[+]?[0-9 ()-]{7,20}$/.test(String(v))) return { ok: false, qid: qq.id, msg: "Please enter a valid phone number." };
        if (qq.type === "numeric") {
          const n = Number(v);
          if (Number.isNaN(n)) return { ok: false, qid: qq.id, msg: "Please enter a number." };
          const vd = qq.validation || {};
          if (vd.min_value != null && n < Number(vd.min_value)) return { ok: false, qid: qq.id, msg: `Minimum is ${vd.min_value}.` };
          if (vd.max_value != null && n > Number(vd.max_value)) return { ok: false, qid: qq.id, msg: `Maximum is ${vd.max_value}.` };
        }
        if (qq.type === "date") {
          const vd = qq.validation || {};
          if (vd.min_date && String(v) < String(vd.min_date)) return { ok: false, qid: qq.id, msg: `Earliest allowed: ${vd.min_date}.` };
          if (vd.max_date && String(v) > String(vd.max_date)) return { ok: false, qid: qq.id, msg: `Latest allowed: ${vd.max_date}.` };
        }
      }
    }
    return { ok: true };
  };

  const submit = async () => {
    if (isPreview) {
      // Preview mode — never persist responses; simulate the thanks step for UX preview
      setThankYouMsg(thankT || "Thank you!");
      setStep("thanks");
      return;
    }
    setBusy(true); setErr("");
    try {
      // Only submit answers for visible questions
      const visibleIds = new Set(survey.questions.filter((qq) => isVisible(qq, answers)).map((qq) => qq.id));
      const payload = {
        answers: Object.entries(answers)
          .filter(([qid]) => visibleIds.has(qid))
          .map(([question_id, value]) => ({ question_id, value })),
        respondent_name: respondent.name || null,
        respondent_email: respondent.email || null,
        respondent_phone: respondent.phone || null,
        metadata: urlMeta,
      };
      const { data } = await axios.post(`${API}/public/surveys/${slug}/respond`, payload);
      setThankYouMsg(data.thank_you_message || "Thank you!");
      setJourney(data.journey || null);
      setStep("thanks");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Could not submit. Please try again.");
    } finally { setBusy(false); }
  };

  // Progress now reflects only visible questions
  const visibleCount = survey.questions.filter((qq) => isVisible(qq, answers)).length || 1;
  const visibleIdx = survey.questions.slice(0, idx + 1).filter((qq) => isVisible(qq, answers)).length;
  const progress = step === "intro" ? 0 : step === "thanks" ? 100 : Math.round((visibleIdx / visibleCount) * 100);

  // Determine if "Next" should say Submit (no more visible questions and no jump to end)
  const remainingVisible = (() => {
    const test = { ...answers };
    let i = idx + 1;
    while (i < survey.questions.length) {
      if (isVisible(survey.questions[i], test)) return true;
      i++;
    }
    return false;
  })();
  const isLast = !remainingVisible;

  const langOptions = ["default", ...Object.keys(survey.translations || {})];
  const brand = survey.brand || {};
  const brandLogo = survey.header_logo_url || brand.logo_url;
  const brandName = brand.name;
  const footerText = brand.footer_text;
  const showProgress = survey.show_progress_bar !== false;
  const isSinglePage = survey.display_mode === "single_page";
  const visibleQs = survey.questions.filter((qq) => isVisible(qq, answers));

  // Theme — fall back to brand defaults if missing
  const theme = survey.theme || {};
  const colors = {
    primary: theme.primary || "#F58220",
    accent: theme.accent || "#F58220",
    background: theme.background || "#FFFFFF",
    text: theme.text || theme.primary || "#1F3A6B",
    buttonText: theme.button_text || "#FFFFFF",
    optionSelectedBg: theme.option_selected_bg || null,
    optionSelectedText: theme.option_selected_text || null,
  };
  const bgImage = theme.background_image_url || "";
  const bgOverlay = Math.max(0, Math.min(1, theme.background_overlay ?? 0.35));
  const themeStyle = {
    "--brand-primary": colors.primary,
    "--brand-accent": colors.accent,
    "--brand-text": colors.text,
    "--brand-btn-text": colors.buttonText,
    "--brand-bg": colors.background,
    color: colors.text,
    ...(bgImage
      ? {
          backgroundImage: `linear-gradient(rgba(0,0,0,${bgOverlay}), rgba(0,0,0,${bgOverlay})), url("${bgImage}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }
      : { backgroundColor: colors.background }),
  };
  const btnStyle = { backgroundColor: colors.primary, color: colors.buttonText };

  return (
    <div className="min-h-screen brand-radial flex flex-col" dir={isRtl ? "rtl" : "ltr"} style={themeStyle}>
      {isPreview && (
        <div data-testid="preview-banner" className="w-full text-center text-[11px] font-bold uppercase tracking-wider py-1.5" style={{ backgroundColor: "#FEF3C7", color: "#92400E" }}>
          Live preview · Submissions are not recorded
        </div>
      )}
      <BrandHeader logo={brandLogo} brandName={brandName} langOptions={langOptions} lang={lang} setLang={setLang} primary={colors.primary} hasBgImage={!!bgImage}/>

      <div className="flex-1 max-w-3xl w-full mx-auto px-4 md:px-6 pb-10">
        {showProgress && (
          <div className="h-2 rounded-full bg-slate-200/70 overflow-hidden mb-6">
            <div className="h-full transition-all" style={{ width: `${progress}%`, background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})` }}/>
          </div>
        )}

        <Card className="p-6 md:p-10 border-0 rounded-3xl bg-white" style={{ boxShadow: "0 24px 48px -22px rgba(31,58,107,0.20)" }}>
          {step === "intro" && (
            <div>
              <RichOrPlain html={titleT} as="h1" inline className="text-3xl md:text-4xl font-black tracking-tight" style={{ color: colors.primary }}/>
              {descT && <RichOrPlain html={descT} as="div" className="mt-2 text-slate-600"/>}
              {welcomeT && <RichOrPlain html={welcomeT} as="div" className="mt-5 text-slate-700"/>}
              <button data-testid="public-start-btn" onClick={() => setStep("q")} className="mt-6 inline-flex items-center gap-2 h-12 px-7 rounded-full font-bold hover:opacity-90 transition-opacity" style={{ ...btnStyle, boxShadow: `0 12px 22px -10px ${colors.primary}80` }}>Start →</button>
            </div>
          )}

          {step === "q" && isSinglePage && (
            <SinglePageView
              survey={survey} tr={tr} answers={answers} setAns={setAns}
              respondent={respondent} setRespondent={setRespondent}
              err={err} busy={busy} colors={colors} btnStyle={btnStyle}
              onSubmit={() => {
                const v = validateAll();
                if (!v.ok) {
                  setErr(v.msg);
                  setTimeout(() => {
                    const el = document.querySelector(`[data-qcard="${v.qid}"]`);
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                  }, 30);
                  return;
                }
                submit();
              }}
              titleT={titleT} descT={descT} welcomeT={welcomeT}
              visibleQs={visibleQs}
            />
          )}

          {step === "q" && !isSinglePage && (
            <div>
              {sectionHere && (
                <div data-testid="public-section-header" className="mb-5 pb-4 border-b border-slate-100">
                  <p className="text-xs uppercase tracking-wider font-bold" style={{ color: colors.accent }}>Section</p>
                  {sectionHere.title && <RichOrPlain html={pipe(sectionHere.title, answers, survey.questions)} as="h3" inline className="mt-1 text-xl font-extrabold" style={{ color: colors.primary }}/>}
                  {sectionHere.description && <RichOrPlain html={pipe(sectionHere.description, answers, survey.questions)} as="div" className="mt-1 text-sm text-slate-600"/>}
                </div>
              )}
              <h2 className="text-2xl font-black" style={{ color: colors.text || colors.primary }}>
                <RichOrPlain html={qTitle} as="span" inline/>
                {q.required && <span className="text-rose-500 ml-1">*</span>}
              </h2>
              {qDesc && <RichOrPlain html={qDesc} as="div" className="mt-1 text-slate-600"/>}

              <div className="mt-6">
                <Renderer q={{ ...q, options: qOptions, placeholder: qPlaceholder }} value={answers[q.id]} onChange={(v) => setAns(q.id, v)} colors={colors} />
              </div>

              {/* Optional respondent fields on first question */}
              {idx === 0 && (
                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Your name (optional)</Label>
                    <Input data-testid="public-respondent-name" value={respondent.name} onChange={(e) => setRespondent((r) => ({ ...r, name: e.target.value }))} className="h-10 mt-1"/>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Email (optional)</Label>
                    <Input data-testid="public-respondent-email" type="email" value={respondent.email} onChange={(e) => setRespondent((r) => ({ ...r, email: e.target.value }))} className="h-10 mt-1"/>
                  </div>
                </div>
              )}

              {err && (
                <div data-testid="public-error" role="alert" className="mt-4 flex items-start gap-2 text-sm px-3 py-2.5 rounded-xl fade-in" style={{ backgroundColor: "var(--detractor-bg)", color: "var(--detractor)", border: "1px solid rgba(220,38,38,0.25)" }}>
                  <span className="font-black">!</span><span className="font-semibold">{err}</span>
                </div>
              )}

              <div className="mt-8 flex justify-between items-center gap-3">
                <Button data-testid="public-back-btn" variant="ghost" onClick={back} className="rounded-full font-semibold px-5">← Back</Button>
                <button data-testid="public-next-btn" disabled={busy} onClick={next} className="inline-flex items-center gap-2 h-12 px-7 rounded-full font-bold hover:opacity-90 disabled:opacity-60 transition-opacity" style={{ ...btnStyle, boxShadow: `0 12px 22px -10px ${colors.primary}80` }}>
                  {isLast ? (busy ? "Submitting…" : "Submit ✓") : "Next →"}
                </button>
              </div>
            </div>
          )}

          {step === "thanks" && (
            <ThanksScreen
              thankYouMsg={thankYouMsg || thankT}
              journey={journey}
              colors={colors}
              btnStyle={btnStyle}
            />
          )}
        </Card>
      </div>

      <BrandFooter logo={brandLogo} brandName={brandName} footerText={footerText} hasBgImage={!!bgImage}/>
    </div>
  );
}

/* Render either rich HTML (sanitized) or plain text. Inline strips the wrapping <p> for headings. */
function RichOrPlain({ html, as: Tag = "div", inline = false, className = "", style }) {
  if (html === null || html === undefined || html === "") return null;
  if (isHtml(String(html))) {
    return <RichText html={String(html)} as={Tag} inline={inline} className={className} {...(style ? { style } : {})}/>;
  }
  return <Tag className={className} {...(style ? { style } : {})}>{String(html)}</Tag>;
}

/* ---------- Post-response Thanks screen (with journey: redirect button + coupon) ---------- */
function ThanksScreen({ thankYouMsg, journey, colors, btnStyle }) {
  const [copied, setCopied] = React.useState(false);
  const coupon = journey?.coupon;
  const redirectUrl = journey?.redirect_url;
  const redirectLabel = journey?.redirect_label || "Continue";

  const copy = async () => {
    if (!coupon?.code) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(coupon.code);
      } else {
        const ta = document.createElement("textarea");
        ta.value = coupon.code;
        ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      // Silent — user can select-all + Ctrl+C on the mono span
    }
  };

  return (
    <div className="text-center py-8 slide-up">
      <div className="w-16 h-16 mx-auto rounded-full grid place-items-center" style={{ backgroundColor: "var(--promoter-bg)", color: "var(--promoter)" }}>
        <CheckCircle2 size={32}/>
      </div>
      <RichOrPlain html={thankYouMsg} as="h2" inline className="mt-5 text-3xl font-black tracking-tight" style={{ color: colors.primary }}/>
      <p className="mt-2 text-sm" style={{ color: "var(--ink-500)" }}>Your feedback helps us improve.</p>

      {coupon?.code && (
        <div className="mt-8 mx-auto max-w-sm rounded-sm p-5 relative" style={{ backgroundColor: "var(--ink-25)", border: "1px dashed var(--border-strong)" }} data-testid="thanks-coupon">
          {coupon.message && (
            <RichOrPlain html={coupon.message} as="div" className="text-sm mb-3" style={{ color: "var(--ink-600)" }}/>
          )}
          <div className="flex items-center justify-between gap-2 rounded-sm px-4 py-3" style={{ backgroundColor: "#FFFFFF", border: "1px solid var(--border)" }}>
            <span className="font-mono text-lg font-bold tracking-widest select-all" data-testid="thanks-coupon-code" style={{ color: "var(--ink-900)" }}>{coupon.code}</span>
            <button
              onClick={copy}
              data-testid="thanks-coupon-copy"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-sm transition"
              style={{ color: copied ? "var(--promoter)" : "var(--ink-600)", backgroundColor: copied ? "var(--promoter-bg)" : "var(--ink-50)" }}
            >
              {copied ? <><Check size={13}/> Copied</> : <><Copy size={13}/> Copy</>}
            </button>
          </div>
          <p className="text-[10px] uppercase tracking-widest font-mono mt-3" style={{ color: "var(--ink-400)" }}>Show this at checkout · Single-use</p>
        </div>
      )}

      {redirectUrl && (
        <a
          href={redirectUrl}
          target="_blank"
          rel="noreferrer"
          data-testid="thanks-redirect-btn"
          className="mt-8 inline-flex items-center gap-2 h-12 px-7 rounded-full font-bold text-sm transition hover:opacity-90"
          style={{ ...btnStyle, boxShadow: `0 12px 22px -10px ${colors.primary}80` }}
        >
          {redirectLabel}
          <ExternalLink size={14}/>
        </a>
      )}
    </div>
  );
}


/* ---------- Brand header & footer ---------- */
function BrandHeader({ logo, brandName, langOptions, lang, setLang, hasBgImage }) {
  const [imgOk, setImgOk] = React.useState(true);
  return (
    <header className={`px-6 py-5 max-w-3xl mx-auto w-full flex items-center justify-between ${hasBgImage ? "rounded-b-lg" : ""}`}
      style={hasBgImage ? { backgroundColor: "rgba(255,255,255,0.75)", backdropFilter: "blur(6px)", marginTop: 0 } : undefined}>
      <div className="flex items-center gap-3" data-testid="public-brand-header">
        {logo && imgOk ? (
          <img src={logo} alt={brandName || "Brand"} className="h-10 w-auto max-w-[180px] object-contain" onError={() => setImgOk(false)}/>
        ) : (
          <Logo/>
        )}
        {logo && brandName && imgOk && <span className="text-sm font-bold text-[#1F3A6B] hidden sm:inline">{brandName}</span>}
      </div>
      {langOptions.length > 1 && (
        <select
          data-testid="public-lang-selector"
          value={lang}
          onChange={(e) => setLang(e.target.value)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white capitalize"
        >
          {langOptions.map((l) => <option key={l} value={l}>{l === "default" ? "English" : l}</option>)}
        </select>
      )}
    </header>
  );
}

function BrandFooter({ logo, brandName, footerText, hasBgImage }) {
  const [imgOk, setImgOk] = React.useState(true);
  return (
    <footer className={`mt-6 ${hasBgImage ? "" : "border-t border-slate-100"}`} style={{ backgroundColor: hasBgImage ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.6)", backdropFilter: "blur(6px)" }}>
      <div className="max-w-3xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center gap-3 sm:justify-between" data-testid="public-brand-footer">
        <div className="flex items-center gap-2">
          {logo && imgOk && <img src={logo} alt={brandName || ""} className="h-7 w-auto max-w-[120px] object-contain opacity-70" onError={() => setImgOk(false)}/>}
          {footerText && <span className="text-xs text-slate-500 whitespace-pre-line leading-relaxed">{footerText}</span>}
        </div>
        <p className="text-center text-xs text-slate-500">
          Powered by <span className="text-[#1F3A6B] font-bold">CX10X<span className="text-[#F58220]">.ai</span></span>
        </p>
      </div>
    </footer>
  );
}

/* ---------- Single-page mode: all visible questions stacked ---------- */
function SinglePageView({ survey, tr, answers, setAns, respondent, setRespondent, err, busy, onSubmit, titleT, descT, welcomeT, visibleQs, colors, btnStyle }) {
  const sectionsByPage = Object.fromEntries((survey.sections || []).map((s) => [s.page, s]));
  // Group questions by page so section headers render between blocks
  const groups = [];
  let currentPage = null;
  for (const q of visibleQs) {
    const p = q.page || 1;
    if (p !== currentPage) {
      groups.push({ page: p, section: sectionsByPage[p], questions: [q] });
      currentPage = p;
    } else {
      groups[groups.length - 1].questions.push(q);
    }
  }
  return (
    <div>
      <RichOrPlain html={titleT} as="h1" inline className="text-3xl font-extrabold" style={{ color: colors.primary }}/>
      {descT && <RichOrPlain html={descT} as="div" className="mt-2 text-slate-600"/>}
      {welcomeT && <RichOrPlain html={welcomeT} as="div" className="mt-3 text-slate-700"/>}

      <div className="mt-6 grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-500">Your name (optional)</Label>
          <Input data-testid="public-respondent-name" value={respondent.name} onChange={(e) => setRespondent((r) => ({ ...r, name: e.target.value }))} className="h-10 mt-1"/>
        </div>
        <div>
          <Label className="text-xs text-slate-500">Email (optional)</Label>
          <Input data-testid="public-respondent-email" type="email" value={respondent.email} onChange={(e) => setRespondent((r) => ({ ...r, email: e.target.value }))} className="h-10 mt-1"/>
        </div>
      </div>

      <div className="mt-8 space-y-7">
        {groups.map((g, gi) => (
          <div key={gi}>
            {g.section && (g.section.title || g.section.description) && (
              <div className="mb-4 pb-3 border-b border-slate-100">
                {g.section.title && <RichOrPlain html={pipe(g.section.title, answers, survey.questions)} as="h3" inline className="text-xl font-extrabold" style={{ color: colors.primary }}/>}
                {g.section.description && <RichOrPlain html={pipe(g.section.description, answers, survey.questions)} as="div" className="mt-1 text-sm text-slate-600"/>}
              </div>
            )}
            <div className="space-y-7">
              {g.questions.map((q) => {
                const qTr = tr?.questions?.[q.id] || {};
                const t = pipe(qTr.title || q.title || "", answers, survey.questions);
                const d = pipe(qTr.description ?? q.description ?? "", answers, survey.questions);
                return (
                  <div key={q.id} data-qcard={q.id} data-testid={`single-q-${q.id}`}>
                    <h3 className="text-lg font-bold" style={{ color: colors.primary }}>
                      <RichOrPlain html={t} as="span" inline/>
                      {q.required && <span className="text-rose-500 ml-1">*</span>}
                    </h3>
                    {d && <RichOrPlain html={d} as="div" className="mt-1 text-sm text-slate-600"/>}
                    <div className="mt-3">
                      <Renderer q={{ ...q, options: qTr.options || q.options, placeholder: qTr.placeholder ?? q.placeholder }} value={answers[q.id]} onChange={(v) => setAns(q.id, v)} colors={colors}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {err && (
        <div data-testid="public-error" role="alert" className="mt-6 flex items-start gap-2 text-sm px-3 py-2.5 rounded-xl fade-in" style={{ backgroundColor: "var(--detractor-bg)", color: "var(--detractor)", border: "1px solid rgba(220,38,38,0.25)" }}>
          <span className="font-black">!</span><span className="font-semibold">{err}</span>
        </div>
      )}

      <div className="mt-8 flex justify-end">
        <button data-testid="public-submit-btn" disabled={busy} onClick={onSubmit} className="inline-flex items-center gap-2 h-12 px-8 rounded-full font-bold hover:opacity-90 disabled:opacity-60 transition-opacity" style={{ ...btnStyle, boxShadow: `0 12px 22px -10px ${colors.primary}80` }}>
          {busy ? "Submitting…" : "Submit ✓"}
        </button>
      </div>
    </div>
  );
}

function Renderer({ q, value, onChange, colors }) {
  const C = colors || { primary: "#F58220", accent: "#F58220", buttonText: "#FFFFFF" };
  if (q.type === "nps" || q.type === "csat" || q.type === "ces") {
    const items = [];
    const min = q.min ?? 0;
    const max = q.max ?? 10;
    for (let i = min; i <= max; i++) items.push(i);
    // Color-code only NPS (0-10); CSAT/CES use neutral selected style
    const isNps = q.type === "nps";
    const colorFor = (n) => {
      if (!isNps) return null;
      if (n <= 6) return { bg: "bg-rose-500", border: "border-rose-500", text: "text-white", hover: "hover:bg-rose-600", soft: "bg-rose-50 border-rose-200 text-rose-600" };
      if (n <= 8) return { bg: "bg-amber-400", border: "border-amber-400", text: "text-white", hover: "hover:bg-amber-500", soft: "bg-amber-50 border-amber-200 text-amber-700" };
      return { bg: "bg-emerald-500", border: "border-emerald-500", text: "text-white", hover: "hover:bg-emerald-600", soft: "bg-emerald-50 border-emerald-200 text-emerald-700" };
    };
    // NPS feedback messages: show descriptive text based on selected score range
    const npsMsgKey = isNps && typeof value === "number"
      ? (value <= 6 ? "detractor" : value <= 8 ? "passive" : "promoter")
      : null;
    const npsMsg = npsMsgKey && q.nps_messages ? q.nps_messages[npsMsgKey] : null;
    const npsMsgColor = npsMsgKey === "detractor" ? "#DC2626" : npsMsgKey === "passive" ? "#D97706" : "#059669";

    return (
      <div>
        <div className="flex flex-wrap gap-1.5 sm:gap-2">
          {items.map((n) => {
            const c = colorFor(n);
            const selected = value === n;
            const cls = isNps
              ? selected
                ? `${c.bg} ${c.border} ${c.text} shadow-sm scale-[1.05]`
                : `${c.soft} ${c.hover} hover:text-white border-2`
              : selected
                ? "bg-[#F58220] text-white border-[#F58220]"
                : "border-slate-200 text-slate-700 hover:border-[#F58220] hover:bg-[#FFF0DF] border";
            return (
              <button
                key={n}
                data-testid={`opt-${q.id}-${n}`}
                onClick={() => onChange(n)}
                className={`w-10 h-10 sm:w-11 sm:h-11 rounded-full font-bold text-sm transition-all ${cls}`}
                aria-label={`Score ${n}`}
              >
                {n}
              </button>
            );
          })}
        </div>
        {isNps && (
          <div className="mt-2 flex justify-between text-[11px] text-slate-500 font-medium px-1">
            <span>Not likely</span>
            <span>Very likely</span>
          </div>
        )}
        {npsMsg && (
          <div
            data-testid={`nps-feedback-${q.id}-${npsMsgKey}`}
            className="mt-4 fade-in"
            style={{ color: npsMsgColor }}
          >
            <RichOrPlain html={npsMsg} as="div" className="rt-render text-[15px] leading-relaxed font-medium"/>
          </div>
        )}
      </div>
    );
  }
  if (q.type === "yes_no") {
    const v = value;
    return (
      <div className="flex gap-3">
        <button
          data-testid={`opt-${q.id}-yes`}
          onClick={() => onChange("yes")}
          className={`flex-1 sm:flex-none flex flex-col items-center gap-2 px-6 py-5 rounded-xl border-2 transition-all ${v === "yes" ? "border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm" : "border-slate-200 text-slate-600 hover:border-emerald-400 hover:bg-emerald-50"}`}
        >
          <ThumbsUp size={32} className={v === "yes" ? "fill-emerald-500 text-emerald-500" : ""}/>
          <span className="font-bold">Yes</span>
        </button>
        <button
          data-testid={`opt-${q.id}-no`}
          onClick={() => onChange("no")}
          className={`flex-1 sm:flex-none flex flex-col items-center gap-2 px-6 py-5 rounded-xl border-2 transition-all ${v === "no" ? "border-rose-500 bg-rose-50 text-rose-700 shadow-sm" : "border-slate-200 text-slate-600 hover:border-rose-400 hover:bg-rose-50"}`}
        >
          <ThumbsDown size={32} className={v === "no" ? "fill-rose-500 text-rose-500" : ""}/>
          <span className="font-bold">No</span>
        </button>
      </div>
    );
  }
  if (q.type === "rating") {
    const max = q.max ?? 5;
    return (
      <div className="flex gap-1.5">
        {[...Array(max)].map((_, i) => {
          const n = i + 1;
          return <button key={n} data-testid={`opt-${q.id}-${n}`} onClick={() => onChange(n)} className="p-1"><Star size={32} className={n <= (value || 0) ? "fill-amber-400 text-amber-400" : "text-slate-300"}/></button>;
        })}
      </div>
    );
  }
  if (q.type === "slider") {
    return (
      <div>
        <input type="range" min={q.min ?? 0} max={q.max ?? 100} value={value ?? Math.round(((q.min ?? 0) + (q.max ?? 100)) / 2)} onChange={(e) => onChange(Number(e.target.value))} className="w-full"/>
        <div className="mt-2 text-center font-bold text-[#1F3A6B] text-lg tabular-nums">{value ?? "—"}</div>
      </div>
    );
  }
  if (q.type === "emoji") {
    const opts = q.options || [];
    // Detect if this is a graded smiley scale (≥3 distinct face emojis) — apply red→green gradient
    const FACE_SET = new Set(["😡","😠","😞","😟","😢","☹️","😐","😶","🙂","😊","😄","😍","🤩","🥰"]);
    const allFaces = opts.length >= 3 && opts.every((o) => FACE_SET.has(o));
    const gradColor = (i, total) => {
      if (total <= 1) return "#10B981";
      const t = i / (total - 1); // 0..1
      // red -> orange -> slate-ish -> lime -> emerald
      const stops = ["#EF4444", "#F97316", "#94A3B8", "#84CC16", "#10B981"];
      const idx = Math.min(stops.length - 1, Math.round(t * (stops.length - 1)));
      return stops[idx];
    };
    return (
      <div className="flex flex-wrap gap-2">
        {opts.map((e, i) => {
          const sel = value === e;
          const isUp = e === "👍" || e === ":thumbs_up:";
          const isDown = e === "👎" || e === ":thumbs_down:";
          const isRed = e === "🔴";
          const isYellow = e === "🟡";
          const isGreen = e === "🟢";
          const isHand = e === "✋";

          // Thumbs: green/red colored Lucide icons
          if (isUp || isDown) {
            const color = isUp ? "#10B981" : "#EF4444";
            const softBg = isUp ? "rgba(16,185,129,0.10)" : "rgba(239,68,68,0.10)";
            return (
              <button
                key={i}
                data-testid={`opt-${q.id}-${i}`}
                onClick={() => onChange(e)}
                className={`w-14 h-14 grid place-items-center border-2 rounded-md transition-all ${sel ? "shadow-sm scale-[1.04]" : "border-slate-200 hover:border-slate-300"}`}
                style={sel ? { borderColor: color, backgroundColor: softBg } : undefined}
                aria-label={isUp ? "Thumbs up" : "Thumbs down"}
              >
                {isUp
                  ? <ThumbsUp size={28} style={{ color, fill: sel ? color : "transparent" }}/>
                  : <ThumbsDown size={28} style={{ color, fill: sel ? color : "transparent" }}/>}
              </button>
            );
          }

          // Traffic light circles: render as large filled circles with their natural color
          if (isRed || isYellow || isGreen) {
            const color = isRed ? "#EF4444" : isYellow ? "#F59E0B" : "#10B981";
            const ring = sel ? color : "#E2E8F0";
            return (
              <button
                key={i}
                data-testid={`opt-${q.id}-${i}`}
                onClick={() => onChange(e)}
                className={`w-14 h-14 grid place-items-center border-2 rounded-full transition-all ${sel ? "shadow-md scale-[1.06]" : "hover:scale-105"}`}
                style={{ borderColor: ring, backgroundColor: sel ? color : `${color}22` }}
                aria-label={isRed ? "Bad" : isYellow ? "Average" : "Good"}
              >
                <span className={`block w-7 h-7 rounded-full transition-all`} style={{ backgroundColor: color, opacity: sel ? 1 : 0.85, boxShadow: sel ? `0 0 0 3px ${color}33` : "none" }}/>
              </button>
            );
          }

          // Hand gesture (neutral OK)
          if (isHand) {
            const color = "#F59E0B";
            return (
              <button
                key={i}
                data-testid={`opt-${q.id}-${i}`}
                onClick={() => onChange(e)}
                className={`w-14 h-14 grid place-items-center border-2 rounded-md text-2xl transition-all ${sel ? "shadow-sm scale-[1.04]" : "border-slate-200 hover:border-slate-300"}`}
                style={sel ? { borderColor: color, backgroundColor: `${color}1A` } : undefined}
                aria-label="Average"
              >
                {e}
              </button>
            );
          }

          // Graded smiley face: positional red→green coloring
          if (allFaces) {
            const color = gradColor(i, opts.length);
            return (
              <button
                key={i}
                data-testid={`opt-${q.id}-${i}`}
                onClick={() => onChange(e)}
                className={`w-14 h-14 grid place-items-center border-2 rounded-md text-2xl transition-all ${sel ? "shadow-sm scale-[1.06]" : "hover:scale-[1.02]"}`}
                style={{
                  borderColor: sel ? color : "#E2E8F0",
                  backgroundColor: sel ? `${color}1A` : "#FFFFFF",
                }}
              >
                {e}
              </button>
            );
          }

          // Default emoji (unknown / mixed)
          return (
            <button key={i} data-testid={`opt-${q.id}-${i}`} onClick={() => onChange(e)} className={`w-14 h-14 grid place-items-center text-2xl border rounded-md transition-all ${sel ? "border-[#F58220] bg-[#FFF0DF]" : "border-slate-200 hover:border-[#F58220]"}`}>{e}</button>
          );
        })}
      </div>
    );
  }
  if (q.type === "mcq" || q.type === "dropdown") {
    const selBg = C.optionSelectedBg || C.primary;
    const selText = C.optionSelectedText || C.buttonText;
    return (
      <div className="space-y-2">
        {(q.options || []).map((o, i) => {
          const sel = value === o;
          return (
            <button
              key={i}
              data-testid={`opt-${q.id}-${i}`}
              onClick={() => onChange(o)}
              className={`w-full text-left border rounded-md px-4 py-3 text-sm font-medium transition-all ${sel ? "border-2 shadow-sm" : "border-slate-200 text-slate-700"}`}
              style={sel ? { borderColor: selBg, color: selText, backgroundColor: selBg } : undefined}
            >{o}</button>
          );
        })}
      </div>
    );
  }
  if (q.type === "multi_select") {
    const selBg = C.optionSelectedBg || C.primary;
    const selText = C.optionSelectedText || C.buttonText;
    const sel = Array.isArray(value) ? value : [];
    const toggle = (o) => onChange(sel.includes(o) ? sel.filter((x) => x !== o) : [...sel, o]);
    return (
      <div className="flex flex-wrap gap-2">
        {(q.options || []).map((o, i) => {
          const on = sel.includes(o);
          return (
            <button
              key={i}
              data-testid={`opt-${q.id}-${i}`}
              onClick={() => toggle(o)}
              className={`px-4 py-2.5 rounded-full text-sm font-semibold border-2 transition-all ${on ? "shadow-sm" : "bg-white border-slate-200 text-slate-700 hover:border-[#F58220] hover:text-[#1F3A6B]"}`}
              style={on ? { backgroundColor: selBg, borderColor: selBg, color: selText } : undefined}
            >
              {on && <span className="mr-1.5">✓</span>}{o}
            </button>
          );
        })}
      </div>
    );
  }
  if (q.type === "ranking") {
    const arr = Array.isArray(value) && value.length === (q.options || []).length ? value : (q.options || []);
    const move = (i, dir) => {
      const j = i + dir;
      if (j < 0 || j >= arr.length) return;
      const a = [...arr]; [a[i], a[j]] = [a[j], a[i]]; onChange(a);
    };
    return (
      <div className="space-y-2">
        {arr.map((o, i) => (
          <div key={o} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2">
            <span className="font-bold text-[#F58220]">{i + 1}.</span>
            <span className="flex-1 text-sm">{o}</span>
            <Button size="icon" variant="ghost" onClick={() => move(i, -1)} className="h-7 w-7">↑</Button>
            <Button size="icon" variant="ghost" onClick={() => move(i, 1)} className="h-7 w-7">↓</Button>
          </div>
        ))}
      </div>
    );
  }
  if (q.type === "text") return <Input data-testid={`opt-${q.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={q.placeholder || ""} className="h-11"/>;
  if (q.type === "long_text") return <Textarea data-testid={`opt-${q.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={q.placeholder || ""} rows={4}/>;
  if (q.type === "email") return <Input data-testid={`opt-${q.id}`} type="email" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={q.placeholder || "you@example.com"} className="h-11"/>;
  if (q.type === "phone") return <Input data-testid={`opt-${q.id}`} type="tel" inputMode="tel" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={q.placeholder || "+1 555 0100"} className="h-11"/>;
  if (q.type === "numeric") {
    const v = q.validation || {};
    return <Input data-testid={`opt-${q.id}`} type="number" inputMode="decimal" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} placeholder={q.placeholder || ""} className="h-11" min={v.min_value ?? undefined} max={v.max_value ?? undefined}/>;
  }
  if (q.type === "date") {
    const v = q.validation || {};
    return <Input data-testid={`opt-${q.id}`} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-11" min={v.min_date || undefined} max={v.max_date || undefined}/>;
  }
  if (q.type === "time") {
    return <Input data-testid={`opt-${q.id}`} type="time" value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-11"/>;
  }
  if (q.type === "image_choice") {
    return (
      <div className="grid grid-cols-2 gap-3">
        {(q.options || []).map((url, i) => (
          <button
            key={i}
            data-testid={`opt-${q.id}-${i}`}
            onClick={() => onChange(url)}
            className={`relative border-2 rounded-lg overflow-hidden transition ${value === url ? "border-[#F58220]" : "border-slate-200 hover:border-[#F58220]"}`}
          >
            <img src={url} alt="" className="w-full h-32 object-cover"/>
            {value === url && <div className="absolute top-2 right-2 bg-[#F58220] text-white rounded-full w-6 h-6 grid place-items-center text-xs">✓</div>}
          </button>
        ))}
      </div>
    );
  }
  if (q.type === "file_upload") {
    const accept = (q.allowed_extensions || []).map((e) => "." + e.replace(/^\./, "")).join(",");
    return (
      <div>
        <input
          data-testid={`opt-${q.id}`}
          type="file"
          accept={accept || undefined}
          onChange={(e) => onChange(e.target.files?.[0]?.name || "")}
          className="block w-full text-sm border border-dashed border-slate-300 rounded-md px-3 py-6 cursor-pointer hover:border-[#F58220]"
        />
        {q.allowed_extensions?.length ? <p className="mt-1 text-[11px] text-slate-500">Allowed: {q.allowed_extensions.join(", ")}</p> : null}
        {value && <p className="mt-2 text-xs text-slate-500">Selected: <span className="font-semibold">{value}</span></p>}
      </div>
    );
  }
  return <p className="text-slate-500 text-sm">Unsupported question type</p>;
}

function FullPage({ children }) {
  return <div className="min-h-screen grid place-items-center brand-radial"><div className="text-slate-700">{children}</div></div>;
}


// Convert #RRGGBB to rgba(r,g,b,alpha)
function hexToRgba(hex, alpha = 1) {
  if (!hex || typeof hex !== "string") return `rgba(0,174,239,${alpha})`;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return `rgba(0,174,239,${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
